var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { DocEditorBlockSpecs, EdgelessEditorBlockSpecs, ThemeObserver, } from '@blocksuite/blocks';
import { noop, Slot } from '@blocksuite/global/utils';
import { ShadowlessElement, WithDisposable } from '@blocksuite/lit';
import { html, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { DocEditor } from './doc-editor.js';
import { EdgelessEditor } from './edgeless-editor.js';
noop(DocEditor);
noop(EdgelessEditor);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function forwardSlot(from, to) {
    Object.entries(from).forEach(([key, slot]) => {
        const target = to[key];
        if (target) {
            slot.pipe(target);
        }
    });
}
let AffineEditorContainer = class AffineEditorContainer extends WithDisposable(ShadowlessElement) {
    constructor() {
        super(...arguments);
        /** Due to product naming, `DocEditor` may be referred to as "page mode" */
        this.mode = 'page';
        this.docSpecs = DocEditorBlockSpecs;
        this.edgelessSpecs = EdgelessEditorBlockSpecs;
        this.autofocus = false;
        this.themeObserver = new ThemeObserver();
        this.slots = {
            pageLinkClicked: new Slot(),
            pageModeSwitched: new Slot(),
            pageUpdated: new Slot(),
            tagClicked: new Slot(),
        };
    }
    get root() {
        return this.mode === 'page'
            ? this._docPage?.host
            : this._edgelessPage?.host;
    }
    get model() {
        return this.page.root;
    }
    async getUpdateComplete() {
        const result = await super.getUpdateComplete();
        const root = this.root;
        if (root) {
            await root.updateComplete;
        }
        return result;
    }
    connectedCallback() {
        super.connectedCallback();
        if (!this.page) {
            throw new Error('Missing page for EditorContainer!');
        }
        // subscribe store
        this._disposables.add(this.page.slots.rootAdded.on(() => {
            // add the 'page' as requesting property to
            // make sure the `forwardSlot` is called in `updated` lifecycle
            this.requestUpdate('page');
        }));
        this.themeObserver.observe(document.documentElement);
        this._disposables.add(this.themeObserver);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
    }
    firstUpdated() {
        if (this.mode === 'page') {
            setTimeout(() => {
                if (this.autofocus) {
                    this._docPage?.titleInlineEditor.focusEnd();
                }
            });
        }
    }
    updated(changedProperties) {
        if (changedProperties.has('mode')) {
            this.slots.pageModeSwitched.emit(this.mode);
        }
        if (changedProperties.has('page')) {
            this.slots.pageUpdated.emit({ newPageId: this.page.id });
        }
        if (!changedProperties.has('page') && !changedProperties.has('mode')) {
            return;
        }
        requestAnimationFrame(() => {
            if (this._docPage)
                forwardSlot(this._docPage.slots, this.slots);
            if (this._edgelessPage)
                forwardSlot(this._edgelessPage.slots, this.slots);
        });
    }
    render() {
        if (!this.model)
            return nothing;
        return html `${keyed(this.model.id, this.mode === 'page'
            ? html `<doc-editor
            .page=${this.page}
            .specs=${this.docSpecs}
          ></doc-editor>`
            : html `<edgeless-editor
            .page=${this.page}
            .specs=${this.edgelessSpecs}
          ></edgeless-editor>`)}`;
    }
};
__decorate([
    property({ attribute: false })
], AffineEditorContainer.prototype, "page", void 0);
__decorate([
    property({ attribute: false })
], AffineEditorContainer.prototype, "mode", void 0);
__decorate([
    property({ attribute: false })
], AffineEditorContainer.prototype, "docSpecs", void 0);
__decorate([
    property({ attribute: false })
], AffineEditorContainer.prototype, "edgelessSpecs", void 0);
__decorate([
    property({ attribute: false })
], AffineEditorContainer.prototype, "autofocus", void 0);
__decorate([
    query('affine-doc-page')
], AffineEditorContainer.prototype, "_docPage", void 0);
__decorate([
    query('affine-edgeless-page')
], AffineEditorContainer.prototype, "_edgelessPage", void 0);
AffineEditorContainer = __decorate([
    customElement('affine-editor-container')
], AffineEditorContainer);
export { AffineEditorContainer };
//# sourceMappingURL=editor-container.js.map